import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { SessionUserSummary } from "@jamroom/shared";
import { apiClient, type AdminActiveSessionItem, type AdminAudioObject, type AdminUsersResponse } from "../api/client";
import { useAuthStore } from "../store/authStore";

type AdminUser = AdminUsersResponse["users"][number];
type ActiveSession = AdminActiveSessionItem;
type SessionUser = SessionUserSummary;

const normalizeGuestKey = (name: string): string =>
  name.trim().replace(/\s+/g, " ").toLowerCase();

export default function AdminPage() {
  const navigate = useNavigate();
  const { me, loading, error, setMe, setLoading, setError } = useAuthStore();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [sessionsIndex, setSessionsIndex] = useState<ActiveSession[]>([]);
  const [sessionsIndexBusy, setSessionsIndexBusy] = useState<boolean>(false);
  const [sessionsIndexFilter, setSessionsIndexFilter] = useState<"ALL" | "ACTIVE" | "EXPIRED">("ACTIVE");
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedSessionUsers, setSelectedSessionUsers] = useState<SessionUser[]>([]);
  const [selectedSessionUsersBusy, setSelectedSessionUsersBusy] = useState<boolean>(false);
  const [selectedSessionUsersBusyKey, setSelectedSessionUsersBusyKey] = useState<string | null>(null);
  const [selectedSessionAddName, setSelectedSessionAddName] = useState<string>("");
  const [selectedSessionExtendHours, setSelectedSessionExtendHours] = useState<number>(1);
  const [audioPrefix, setAudioPrefix] = useState<string>("");
  const [audioObjects, setAudioObjects] = useState<AdminAudioObject[]>([]);
  const [audioCursor, setAudioCursor] = useState<string | null>(null);
  const [audioBusy, setAudioBusy] = useState<boolean>(false);
  const [audioSelected, setAudioSelected] = useState<Record<string, boolean>>({});
  const [deleteSessionId, setDeleteSessionId] = useState<string>("");

  const isOwner = me?.role === "OWNER" && me?.status === "APPROVED";

  const headerLabel = useMemo(() => {
    if (!me) return "Unknown";
    return `${me.email ?? ""} (${me.role} / ${me.status})`;
  }, [me]);

  const visibleSessions = useMemo(() => {
    const filtered =
      sessionsIndexFilter === "ALL"
        ? sessionsIndex
        : sessionsIndex.filter((s) => s.status === sessionsIndexFilter);

    return [...filtered].sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""));
  }, [sessionsIndex, sessionsIndexFilter]);

  const selectedSession = useMemo(() => {
    if (!selectedSessionId) return null;
    return sessionsIndex.find((s) => s.sessionId === selectedSessionId) || null;
  }, [selectedSessionId, sessionsIndex]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .me()
      .then((res) => {
        if (cancelled) return;
        setMe(res);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load identity");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [setError, setLoading, setMe]);

  const refreshUsers = async () => {
    setLocalError(null);
    const res = await apiClient.adminListUsers();
    setUsers(res.users || []);
  };

  const refreshSessions = async () => {
    setLocalError(null);
    setSessionsIndexBusy(true);
    try {
      const res = await apiClient.adminListActiveSessions();
      setSessionsIndex(res.sessions || []);
    } finally {
      setSessionsIndexBusy(false);
    }
  };

  const refreshSelectedSessionUsers = async (sid?: string | null) => {
    const target = sid ?? selectedSessionId;
    if (!target) return;
    setLocalError(null);
    setSelectedSessionUsersBusy(true);
    try {
      const res = await apiClient.adminListSessionUsers(target);
      setSelectedSessionUsers(res.users || []);
    } finally {
      setSelectedSessionUsersBusy(false);
    }
  };

  const selectSession = async (sid: string) => {
    setSelectedSessionId(sid);
    setSelectedSessionUsers([]);
    setSelectedSessionUsersBusyKey(null);
    setSelectedSessionAddName("");
    setSelectedSessionExtendHours(1);
    try {
      await refreshSelectedSessionUsers(sid);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : "Failed to load session users");
    }
  };

  const actOnSelectedSessionUser = async (
    displayName: string,
    action: "approve" | "remove" | "reapprove" | "add"
  ) => {
    if (!selectedSessionId) return;
    const busyKey = normalizeGuestKey(displayName);
    try {
      setSelectedSessionUsersBusyKey(busyKey);
      setLocalError(null);
      await apiClient.adminSessionUserAction(selectedSessionId, action, displayName);
      await refreshSelectedSessionUsers(selectedSessionId);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : "Session user action failed");
    } finally {
      setSelectedSessionUsersBusyKey(null);
    }
  };

  const extendSelectedSession = async (hoursToAdd: number) => {
    if (!selectedSessionId) return;
    try {
      setLocalError(null);
      await apiClient.adminExtendSession(selectedSessionId, hoursToAdd);
      await refreshSessions();
      await refreshSelectedSessionUsers(selectedSessionId);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : "Failed to extend session");
    }
  };

  const refreshAudio = async (mode: "reset" | "more" = "reset") => {
    setLocalError(null);
    setAudioBusy(true);
    try {
      const cursor = mode === "more" ? audioCursor || undefined : undefined;
      const res = await apiClient.adminListAudio({
        prefix: audioPrefix.trim() || undefined,
        cursor,
        limit: 200,
      });
      setAudioCursor(res.cursor);
      setAudioObjects((prev) => (mode === "more" ? [...prev, ...(res.objects || [])] : (res.objects || [])));
      if (mode === "reset") setAudioSelected({});
    } finally {
      setAudioBusy(false);
    }
  };

  useEffect(() => {
    if (!isOwner) return;
    void refreshUsers().catch((e) => {
      setLocalError(e instanceof Error ? e.message : "Failed to load users");
    });
    void refreshSessions().catch((e) => {
      setLocalError(e instanceof Error ? e.message : "Failed to load sessions");
    });
  }, [isOwner]);

  useEffect(() => {
    if (!isOwner) return;
    if (!selectedSessionId) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await apiClient.adminListSessionUsers(selectedSessionId);
        if (cancelled) return;
        setSelectedSessionUsers(res.users || []);
      } catch {
        // ignore
      }
    };

    void poll();
    const id = window.setInterval(() => void poll(), 2500);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [isOwner, selectedSessionId]);

  const act = async (userId: number, action: "approve" | "reject" | "ban") => {
    try {
      setBusyId(userId);
      setLocalError(null);
      if (action === "approve") await apiClient.adminApproveUser(userId);
      if (action === "reject") await apiClient.adminRejectUser(userId);
      if (action === "ban") await apiClient.adminBanUser(userId);
      await refreshUsers();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : "Admin action failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "radial-gradient(circle at top, #283247 0%, #151922 48%, #0f1118 100%)",
        color: "#e2e8f3",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "min(980px, 100%)",
          margin: "0 auto",
          borderRadius: 14,
          border: "1px solid #3a4660",
          background: "#1c2331",
          padding: 24,
          boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
          display: "grid",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img
              src="/branding/jamroom-mark-180.png"
              alt=""
              aria-hidden="true"
              style={{
                width: 30,
                height: 30,
                borderRadius: 12,
                boxShadow: "0 14px 36px rgba(0, 212, 255, 0.12), 0 14px 36px rgba(255, 43, 214, 0.1)",
              }}
            />
            <img
              src="/branding/jamroom-wordmark.png"
              alt="JamRoom"
              style={{
                height: 22,
                width: "auto",
                filter: "drop-shadow(0 18px 48px rgba(0, 212, 255, 0.14))",
              }}
            />
            <div style={{ fontFamily: "var(--jr-font-display)", fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", fontSize: 12, color: "#b9c5db" }}>
              Admin
            </div>
          </div>
          <div style={{ fontSize: 12, color: "#b9c5db" }}>
            {loading ? "Checking Access..." : headerLabel}
          </div>
        </div>

        {error && <div style={{ color: "#ffb3b3" }}>{error}</div>}
        {localError && <div style={{ color: "#ffb3b3" }}>{localError}</div>}

        {!isOwner && (
          <div style={{ padding: 12, border: "1px solid #5a4721", borderRadius: 10, background: "#2a2417" }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Owner access required</div>
            <div style={{ color: "#d9c69c", fontSize: 13 }}>
              This page is only available to the configured owner account.
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="jr-btn jr-btn--ghost"
          >
            Back
          </button>

          {isOwner && (
            <button
              type="button"
              onClick={() => refreshUsers().catch((e) => setLocalError(e instanceof Error ? e.message : String(e)))}
              className="jr-btn jr-btn--primary"
            >
              Refresh users
            </button>
          )}
        </div>

        {isOwner && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#b9c5db" }}>
                  <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>ID</th>
                  <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Email</th>
                  <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Role</th>
                  <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Status</th>
                  <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Updated</th>
                  <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isBusy = busyId === u.id;
                  return (
                    <tr key={u.id} style={{ borderBottom: "1px solid #222a39" }}>
                      <td style={{ padding: "10px 8px", color: "#b9c5db" }}>{u.id}</td>
                      <td style={{ padding: "10px 8px" }}>{u.email}</td>
                      <td style={{ padding: "10px 8px", color: "#b9c5db" }}>{u.role}</td>
                      <td style={{ padding: "10px 8px" }}>{u.status}</td>
                      <td style={{ padding: "10px 8px", color: "#b9c5db" }}>{u.updatedAt}</td>
                      <td style={{ padding: "10px 8px" }}>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => act(u.id, "approve")}
                            className="jr-btn jr-btn--success jr-btn--sm"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => act(u.id, "reject")}
                            className="jr-btn jr-btn--warn jr-btn--sm"
                          >
                            Set pending
                          </button>
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => act(u.id, "ban")}
                            className="jr-btn jr-btn--danger jr-btn--sm"
                          >
                            Ban
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr>
                    <td style={{ padding: "10px 8px", color: "#b9c5db" }} colSpan={6}>
                      No users yet. Users appear after hitting any authenticated endpoint.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {isOwner && (
          <div style={{ display: "grid", gap: 10, marginTop: 18 }}>
            <h3 style={{ margin: "10px 0 0" }}>Active sessions</h3>
            <div style={{ color: "#b9c5db", fontSize: 13 }}>
              Sessions are indexed best-effort (Durable Objects + D1). Use this view to monitor and manage live rooms.
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                disabled={sessionsIndexBusy}
                onClick={() => refreshSessions().catch((e) => setLocalError(e instanceof Error ? e.message : String(e)))}
                className="jr-btn jr-btn--primary"
              >
                {sessionsIndexBusy ? "Loading..." : "Refresh sessions"}
              </button>

              <label style={{ display: "flex", gap: 8, alignItems: "center", color: "#b9c5db", fontSize: 12 }}>
                Filter
                <select
                  value={sessionsIndexFilter}
                  onChange={(e) => setSessionsIndexFilter(e.target.value as any)}
                  style={{
                    background: "#121722",
                    color: "#e2e8f3",
                    borderRadius: 10,
                    padding: "8px 10px",
                    border: "1px solid #3f4a5c",
                  }}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="ALL">All</option>
                </select>
              </label>

              {selectedSessionId && (
                <button
                  type="button"
                  className="jr-btn jr-btn--ghost"
                  onClick={() => {
                    setSelectedSessionId(null);
                    setSelectedSessionUsers([]);
                    setSelectedSessionUsersBusyKey(null);
                    setSelectedSessionAddName("");
                  }}
                >
                  Clear selection
                </button>
              )}
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#b9c5db" }}>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Session</th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Title</th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Status</th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Online</th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Private</th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Approval</th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Expires</th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Updated</th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleSessions.map((s) => {
                    const isSelected = selectedSessionId === s.sessionId;
                    const expiresText = s.expiresAt ? new Date(s.expiresAt).toLocaleString() : "";
                    const updatedText = s.updatedAt ? new Date(s.updatedAt).toLocaleString() : "";
                    const statusColor = s.status === "ACTIVE" ? "#b6fbd9" : "#ffb3b3";
                    return (
                      <tr key={s.sessionId} style={{ borderBottom: "1px solid #222a39" }}>
                        <td style={{ padding: "10px 8px", fontFamily: "monospace", fontSize: 12, color: "#b9c5db" }}>
                          {s.sessionId}
                        </td>
                        <td style={{ padding: "10px 8px" }}>{s.title}</td>
                        <td style={{ padding: "10px 8px", color: statusColor, fontWeight: 700 }}>{s.status}</td>
                        <td style={{ padding: "10px 8px", color: "#b9c5db" }}>{s.usersOnline}</td>
                        <td style={{ padding: "10px 8px", color: "#b9c5db" }}>{s.isPrivate ? "Yes" : "No"}</td>
                        <td style={{ padding: "10px 8px", color: "#b9c5db" }}>{s.requiresApproval ? "Yes" : "No"}</td>
                        <td style={{ padding: "10px 8px", color: "#b9c5db" }}>{expiresText}</td>
                        <td style={{ padding: "10px 8px", color: "#b9c5db" }}>{updatedText}</td>
                        <td style={{ padding: "10px 8px" }}>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button
                              type="button"
                              className="jr-btn jr-btn--ghost jr-btn--sm"
                              onClick={() => navigate(`/s/${encodeURIComponent(s.sessionId)}`)}
                            >
                              Open
                            </button>
                            <button
                              type="button"
                              className={`jr-btn ${isSelected ? "jr-btn--primary" : "jr-btn--ghost"} jr-btn--sm`}
                              onClick={() => void selectSession(s.sessionId)}
                            >
                              Users
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleSessions.length === 0 && (
                    <tr>
                      <td style={{ padding: "10px 8px", color: "#b9c5db" }} colSpan={9}>
                        No sessions indexed yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {selectedSessionId && (
              <div style={{ padding: 12, border: "1px solid #2d3647", borderRadius: 12, background: "#121722", display: "grid", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ fontWeight: 800 }}>
                    Session users: <span style={{ fontFamily: "monospace" }}>{selectedSessionId}</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="jr-btn jr-btn--primary jr-btn--sm"
                      disabled={selectedSessionUsersBusy}
                      onClick={() => refreshSelectedSessionUsers(selectedSessionId).catch(() => {})}
                    >
                      Refresh users
                    </button>
                    <button
                      type="button"
                      className="jr-btn jr-btn--ghost jr-btn--sm"
                      onClick={() => {
                        setSelectedSessionId(null);
                        setSelectedSessionUsers([]);
                        setSelectedSessionUsersBusyKey(null);
                        setSelectedSessionAddName("");
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>

                {selectedSession && (
                  <div style={{ color: "#b9c5db", fontSize: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <div>Title: {selectedSession.title}</div>
                    <div>Expires: {new Date(selectedSession.expiresAt).toLocaleString()}</div>
                    <div>Online: {selectedSession.usersOnline}</div>
                    <div>Approval: {selectedSession.requiresApproval ? "Yes" : "No"}</div>
                  </div>
                )}

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <label style={{ display: "flex", gap: 8, alignItems: "center", color: "#b9c5db", fontSize: 12 }}>
                    Extend (hours)
                    <input
                      type="number"
                      min={1}
                      max={8}
                      step={1}
                      value={selectedSessionExtendHours}
                      onChange={(e) =>
                        setSelectedSessionExtendHours(Math.max(1, Math.min(8, Number(e.target.value || 1))))
                      }
                      style={{
                        width: 90,
                        borderRadius: 10,
                        border: "1px solid #3f4a5c",
                        background: "#0f1420",
                        color: "#e2e8f3",
                        padding: "8px 10px",
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="jr-btn jr-btn--primary"
                    disabled={selectedSessionUsersBusy}
                    onClick={() => void extendSelectedSession(selectedSessionExtendHours)}
                  >
                    Extend
                  </button>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <input
                    value={selectedSessionAddName}
                    onChange={(e) => setSelectedSessionAddName(e.target.value)}
                    placeholder="Name (pre-approve)"
                    style={{
                      flex: "1 1 260px",
                      minWidth: 220,
                      borderRadius: 10,
                      border: "1px solid #3f4a5c",
                      background: "#0f1420",
                      color: "#e2e8f3",
                      padding: "10px 12px",
                    }}
                  />
                  <button
                    type="button"
                    className="jr-btn jr-btn--success"
                    disabled={!selectedSessionAddName.trim() || selectedSessionUsersBusy}
                    onClick={() => {
                      const v = selectedSessionAddName.trim();
                      if (!v) return;
                      setSelectedSessionAddName("");
                      void actOnSelectedSessionUser(v, "add");
                    }}
                  >
                    Add
                  </button>
                </div>

                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: "#b9c5db" }}>
                        <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Name</th>
                        <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Status</th>
                        <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Conn</th>
                        <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>REC</th>
                        <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Joined</th>
                        <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Last activity</th>
                        <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSessionUsers.map((u) => {
                        const isBusy = selectedSessionUsersBusyKey === u.guestNameNormalized;
                        const joined = u.joinedAt ? new Date(u.joinedAt).toLocaleString() : "";
                        const last = u.lastActivityAt
                          ? new Date(u.lastActivityAt).toLocaleString()
                          : u.lastSeenAt
                            ? new Date(u.lastSeenAt).toLocaleString()
                            : "";
                        return (
                          <tr key={u.guestNameNormalized} style={{ borderBottom: "1px solid #222a39" }}>
                            <td style={{ padding: "10px 8px" }}>
                              {u.displayName}
                            </td>
                            <td style={{ padding: "10px 8px", color: "#b9c5db" }}>{u.status}</td>
                            <td style={{ padding: "10px 8px", color: u.connected ? "#b6fbd9" : "#d9c69c" }}>
                              {u.connected ? "connected" : "disconnected"}
                            </td>
                            <td style={{ padding: "10px 8px", color: u.isRecording ? "#ffb3b3" : "#b9c5db", fontWeight: 800 }}>
                              {u.isRecording ? "REC" : ""}
                            </td>
                            <td style={{ padding: "10px 8px", color: "#b9c5db" }}>{joined}</td>
                            <td style={{ padding: "10px 8px", color: "#b9c5db" }}>{last}</td>
                            <td style={{ padding: "10px 8px" }}>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {u.status === "pending" && (
                                  <button
                                    type="button"
                                    disabled={isBusy}
                                    className="jr-btn jr-btn--success jr-btn--sm"
                                    onClick={() => void actOnSelectedSessionUser(u.displayName, "approve")}
                                  >
                                    Approve
                                  </button>
                                )}
                                {u.status === "removed" && (
                                  <button
                                    type="button"
                                    disabled={isBusy}
                                    className="jr-btn jr-btn--success jr-btn--sm"
                                    onClick={() => void actOnSelectedSessionUser(u.displayName, "reapprove")}
                                  >
                                    Reapprove
                                  </button>
                                )}
                                {u.status !== "removed" && (
                                  <button
                                    type="button"
                                    disabled={isBusy}
                                    className="jr-btn jr-btn--danger jr-btn--sm"
                                    onClick={() => void actOnSelectedSessionUser(u.displayName, "remove")}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {selectedSessionUsers.length === 0 && (
                        <tr>
                          <td style={{ padding: "10px 8px", color: "#b9c5db" }} colSpan={7}>
                            No guests yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {isOwner && (
          <div style={{ display: "grid", gap: 10, marginTop: 18 }}>
            <h3 style={{ margin: "10px 0 0" }}>Storage (R2 Audio)</h3>
            <div style={{ color: "#b9c5db", fontSize: 13 }}>
              Lista objetos del bucket `jamroom-audio`. Puedes filtrar por prefijo, por ejemplo:
              <span style={{ fontFamily: "monospace" }}> sessions/&lt;sessionId&gt;/</span>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <input
                value={audioPrefix}
                onChange={(e) => setAudioPrefix(e.target.value)}
                placeholder="prefix (optional)"
                style={{
                  flex: "1 1 320px",
                  minWidth: 240,
                  borderRadius: 8,
                  border: "1px solid #3f4a5c",
                  background: "#121722",
                  color: "#e2e8f3",
                  padding: "10px 12px",
                }}
              />
              <button
                type="button"
                disabled={audioBusy}
                onClick={() => refreshAudio("reset").catch((e) => setLocalError(e instanceof Error ? e.message : String(e)))}
                className="jr-btn jr-btn--primary"
              >
                {audioBusy ? "Loading..." : "Refresh audio"}
              </button>
              <button
                type="button"
                disabled={audioBusy || !audioCursor}
                onClick={() => refreshAudio("more").catch((e) => setLocalError(e instanceof Error ? e.message : String(e)))}
                className="jr-btn jr-btn--ghost"
              >
                Load more
              </button>
              <button
                type="button"
                disabled={audioBusy || Object.values(audioSelected).filter(Boolean).length === 0}
                onClick={async () => {
                  const keys = Object.entries(audioSelected).filter(([, v]) => v).map(([k]) => k);
                  const ok = window.confirm(`Delete ${keys.length} object(s) from R2? This cannot be undone.`);
                  if (!ok) return;
                  try {
                    setAudioBusy(true);
                    await apiClient.adminDeleteAudio(keys);
                    await refreshAudio("reset");
                  } catch (e) {
                    setLocalError(e instanceof Error ? e.message : "Failed to delete audio objects");
                  } finally {
                    setAudioBusy(false);
                  }
                }}
                className="jr-btn jr-btn--danger"
              >
                Delete selected
              </button>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#b9c5db" }}>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Sel</th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Key</th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Size</th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Uploaded</th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #2d3647" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {audioObjects.map((o) => {
                    const checked = Boolean(audioSelected[o.key]);
                    const sizeMb = (o.size / (1024 * 1024)).toFixed(2);
                    return (
                      <tr key={o.key} style={{ borderBottom: "1px solid #222a39" }}>
                        <td style={{ padding: "10px 8px" }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => setAudioSelected((prev) => ({ ...prev, [o.key]: e.target.checked }))}
                          />
                        </td>
                        <td style={{ padding: "10px 8px", fontFamily: "monospace", fontSize: 12 }}>{o.key}</td>
                        <td style={{ padding: "10px 8px", color: "#b9c5db" }}>{sizeMb} MB</td>
                        <td style={{ padding: "10px 8px", color: "#b9c5db" }}>{o.uploaded || ""}</td>
                        <td style={{ padding: "10px 8px" }}>
                          <button
                            type="button"
                            disabled={audioBusy}
                            onClick={async () => {
                              const ok = window.confirm(`Delete object?\n\n${o.key}`);
                              if (!ok) return;
                              try {
                                setAudioBusy(true);
                                await apiClient.adminDeleteAudio([o.key]);
                                await refreshAudio("reset");
                              } catch (e) {
                                setLocalError(e instanceof Error ? e.message : "Failed to delete object");
                              } finally {
                                setAudioBusy(false);
                              }
                            }}
                            className="jr-btn jr-btn--danger jr-btn--sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {audioObjects.length === 0 && <div style={{ color: "#b9c5db", padding: "10px 0" }}>No objects loaded.</div>}
            </div>
          </div>
        )}

        {isOwner && (
          <div style={{ display: "grid", gap: 10, marginTop: 18 }}>
            <h3 style={{ margin: "10px 0 0" }}>Sessions (Admin Delete)</h3>
            <div style={{ color: "#b9c5db", fontSize: 13 }}>
              Borra una sesion completa por `sessionId`. Esto elimina el estado del Durable Object y borra los audios en R2
              bajo el prefijo <span style={{ fontFamily: "monospace" }}>sessions/&lt;sessionId&gt;/</span>.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <input
                value={deleteSessionId}
                onChange={(e) => setDeleteSessionId(e.target.value)}
                placeholder="sessionId (ej: s_...)"
                style={{
                  flex: "1 1 320px",
                  minWidth: 240,
                  borderRadius: 8,
                  border: "1px solid #3f4a5c",
                  background: "#121722",
                  color: "#e2e8f3",
                  padding: "10px 12px",
                }}
              />
              <button
                type="button"
                disabled={audioBusy || !deleteSessionId.trim()}
                onClick={async () => {
                  const sid = deleteSessionId.trim();
                  if (!sid) return;
                  const ok = window.confirm(
                    `Delete session as admin?\n\nThis cannot be undone.\nSession: ${sid}`
                  );
                  if (!ok) return;
                  try {
                    setLocalError(null);
                    setAudioBusy(true);
                    await apiClient.adminDeleteSession(sid);
                    setDeleteSessionId("");
                    // Refresh audio list if user was filtering by this session.
                    if (audioPrefix.trim().includes(sid)) {
                      await refreshAudio("reset");
                    }
                  } catch (e) {
                    setLocalError(e instanceof Error ? e.message : "Failed to delete session");
                  } finally {
                    setAudioBusy(false);
                  }
                }}
                className="jr-btn jr-btn--danger"
              >
                Delete session
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
